import React, { useMemo } from 'react';
import { Col, FormGroup, Label } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import { NO, YES } from 'common/data/constants';
import {
  StyledColBold,
  FormikInput,
  StyledField,
  Accordion,
  ToggleSelector,
} from 'components/Common';
import { isUserOperator } from 'helpers/jwt_helper';
import { SelectCountry } from 'components/SelectCountry/SelectCountry';

const formFields = [
  {
    type: 'text',
    name: 'upsName',
    key: 3.1,
    label: 'delivery.name',
    placeholder: 'delivery.namePlaceholder',
    maxLength: 50,
  },

  {
    type: 'text',
    name: 'upsAddressLineFirst',
    key: 3.2,
    label: 'client.addressLineFirst',
    placeholder: 'client.addressLineFirstPlaceholder',
  },

  {
    type: 'text',
    name: 'upsCity',
    key: 3.3,
    label: 'client.city',
    placeholder: 'client.cityPlaceholder',
  },

  {
    type: 'text',
    name: 'upsState',
    key: 3.4,
    label: 'client.state',
    placeholder: 'client.statePlaceholder',
  },

  {
    type: 'text',
    name: 'upsPostalCode',
    key: 3.5,
    label: 'client.zipCode',
    placeholder: 'client.zipCodePlaceholder',
    maxLength: 15,
  },
];

export const ApplicantUPSForm = ({
  index, // The index of the current applicant in the applicants array
  isApplicantInWork, // Boolean indicating if applicant has locked services
}) => {
  const { t } = useTranslation();
  const formik = useFormikContext();

  // Check if fields are disabled (order is not editable OR applicant is in work)
  const isDisabledFields = !formik.values.orderDetail?.isEditable;
  const disabled = (!isUserOperator && isApplicantInWork) || isDisabledFields;

  // Get the current applicant data from formik state
  // This replaces the old 'currentApplicant' prop
  const applicant = formik.values.applicants?.[index];

  /**
   * UPS LABEL OPTIONS
   * Using useMemo to prevent recreating the array on every render
   * Translations are applied using t() function
   * Each option has:
   *   - value: the actual value to store ('yes' or 'no')
   *   - label: the display text (translated 'Yes' or 'No')
   *   - icon: the icon to show next to the label
   */
  const UPS_LABEL_OPTIONS = useMemo(
    () => [
      { value: YES, label: t('order.yes'), icon: 'bx bx-check-circle' },
      { value: NO, label: t('order.no'), icon: 'bx bx-x-circle' },
    ],
    [t],
  );

  // Get the current UPS label value from formik
  // If it doesn't exist, default to 'no'
  const upsLabelValue = applicant?.upsLabel || NO;

  /**
   * Why this is simpler than before:
   *
   * OLD WAY (with Select component):
   * value={YES_NO_OPTIONS.filter(option => {
   *   if (currentApplicant?.upsLabel) {
   *     return option.value === currentApplicant.upsLabel;
   *   }
   *   return option.value === NO;
   * })}
   *
   * This was complex because Select needs the full option object.
   *
   * NEW WAY (with ToggleSelector):
   * value={upsLabelValue || NO}
   *
   * This is simpler because ToggleSelector only needs the value string.
   * It handles finding and displaying the matching option internally.
   *
   * OLD WAY (onChange):
   * onChange={option => {
   *   formik.setFieldValue(`applicants[${index}].upsLabel`, option.value);
   * }}
   *
   * NEW WAY (onChange):
   * onChange={(value) => {
   *   formik.setFieldValue(`applicants[${index}].upsLabel`, value);
   * }}
   *
   * ToggleSelector's onChange gives us just the value string,
   * not the whole option object.
   */

  return (
    <>
      {/* Section Header */}
      <Col xs={12} className="mt-4">
        <StyledColBold className="display-4">
          {t('ups.sendDocuments')}
        </StyledColBold>
      </Col>

      {/* Shipping Label Toggle */}
      <FormGroup row className="align-items-center mt-4">
        <Col xs={12} xl={2}>
          <Label>{t('ups.useShippingLabel')}</Label>
        </Col>
        <Col xs={10} xl={10}>
          {/* 
            ToggleSelector replaces the old Select component
            - options: array of { value, label, icon }
            - value: just the string value ('yes' or 'no')
            - onChange: receives just the value string
            - disabled: disables the entire toggle
            - maxWidth: controls the width of the toggle
            - required: makes it a required field
            - theme: uses accordion theme for consistency
            - invalid: shows error state
            - errorMessage: shows error message
          */}
          <ToggleSelector
            options={UPS_LABEL_OPTIONS}
            value={upsLabelValue}
            onChange={value => {
              // value is just 'yes' or 'no'
              formik.setFieldValue(`applicants[${index}].upsLabel`, value);
            }}
            disabled={disabled}
            maxWidth="200px"
            required={true}
            theme="secondary"
            invalid={
              !!formik.errors.applicants?.[index]?.upsLabel &&
              formik.touched.applicants?.[index]?.upsLabel
            }
            errorMessage={t('validation.required')}
          />
        </Col>
      </FormGroup>

      {/* Shipping Address Form - Only shown when UPS Label is 'yes' */}
      {applicant?.upsLabel === YES && (
        <StyledField className="w-100 mt-4 p-4">
          <Accordion
            title="order.enterAddress"
            theme="secondary"
            icon="bx bx-package"
            defaultOpen
          >
            <div className="d-flex flex-wrap mt-4">
              {/* Render all address form fields */}
              {formFields.map(field => (
                <Col key={field.id} xs={12}>
                  <FormikInput
                    name={`applicants[${index}].${field.name}`}
                    disabled={disabled}
                    value={formik.values.applicants?.[index]?.[field.name]}
                    label={field.label}
                    placeholder={field.placeholder}
                    handleChange={formik.handleChange}
                    errors={formik.errors.applicants?.[index]?.[field.name]}
                  />
                </Col>
              ))}

              {/* Country Select */}
              <Col xs={12}>
                <FormikInput
                  label="common.country"
                  name={`applicants[${index}].upsCountry`}
                  errors={formik.errors.applicants?.[index]?.upsCountry}
                  customInput={
                    <SelectCountry
                      isDisabled={disabled}
                      isAvailableUPSLabel
                      invalid={formik.errors.applicants?.[index]?.upsCountry}
                      onChange={country =>
                        formik.setFieldValue(
                          `applicants[${index}].upsCountry`,
                          country?.value,
                        )
                      }
                      value={formik.values.applicants?.[index]?.upsCountry}
                      name="upsCountry"
                    />
                  }
                />
              </Col>
            </div>
          </Accordion>
        </StyledField>
      )}

      <hr className="mt-4 mb-4" />
    </>
  );
};
